import { useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { Camera, Upload, FileText, FileUp, X, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import CaptureDialog from "@/components/CaptureDialog";

function OptionButton({ icon: Icon, title, hint, onClick, disabled, primary, testid }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
      className={`w-full min-h-[56px] rounded-xl px-4 py-3 flex items-center gap-3 text-sm active:scale-[0.98] transition-transform ${primary ? "bg-gold text-navy font-semibold" : "bg-white border border-navy/10 text-navy font-medium"}`}>
      <Icon className={`w-5 h-5 ${primary ? "" : "text-navy/50"}`} />
      <div className="text-left flex-1">
        <p>{title}</p>
        <p className={`text-[11px] font-normal ${primary ? "text-navy/60" : "text-navy/50"}`}>{hint}</p>
      </div>
    </button>
  );
}

export default function DocumentUpload({ kind = "invoice", ocrKind, storeId, onUpload, onOcrResult, onRemove, disabled }) {
  const [uploading, setUploading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState("");
  const [pageCount, setPageCount] = useState(0);
  const [documentId, setDocumentId] = useState(null);
  const [captureSrc, setCaptureSrc] = useState(null);
  const scanRef = useRef(null);
  const photoRef = useRef(null);
  const pdfRef = useRef(null);
  const noun = kind === "receipt" ? "Receipt" : kind === "z_report" ? "Z Report" : "Invoice";

  const upload = useCallback(async (file, previewUrl = null, pages = 0) => {
    if (!file) return false;
    if (!storeId) { toast.error("Please select a store first"); return false; }
    setPreview(previewUrl || (file.type.startsWith("image/") ? URL.createObjectURL(file) : null));
    setFileName(file.name);
    setPageCount(pages);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(`/documents/upload?store_id=${storeId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocumentId(res.data.id);
      if (onUpload) onUpload(res.data);
      toast.success("Document uploaded");
      return true;
    } catch {
      toast.error("Upload failed");
      setPreview(null);
      setFileName("");
      return false;
    } finally {
      setUploading(false);
    }
  }, [storeId, onUpload]);

  const onCaptureFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) setCaptureSrc(URL.createObjectURL(f));
  };

  const onCaptureConfirm = async (file, firstPage, pages) => {
    if (await upload(file, firstPage, pages)) setCaptureSrc(null);
  };

  const runOcr = async () => {
    if (!documentId) return;
    setScanning(true);
    try {
      const res = await api.post(`/documents/${documentId}/ocr${ocrKind ? `?kind=${ocrKind}` : ""}`);
      if (onOcrResult) onOcrResult(res.data.ocr_data);
      if (res.data.ocr_data?.error) toast.error("Could not extract data");
      else toast.success("Data extracted — please verify");
    } catch {
      toast.error("Extraction failed");
    } finally {
      setScanning(false);
    }
  };

  const clear = () => {
    setPreview(null);
    setFileName("");
    setPageCount(0);
    setDocumentId(null);
    if (onRemove) onRemove(documentId);
  };

  if (documentId) {
    return (
      <div className="bg-white rounded-xl border border-navy/10 p-3 space-y-3" data-testid="uploaded-document-card">
        <div className="flex items-start gap-3">
          {preview ? (
            <img src={preview} alt="Preview" className="w-20 h-20 rounded-lg object-cover border border-navy/10" data-testid="uploaded-document-preview" />
          ) : (
            <div className="w-20 h-20 rounded-lg bg-navy/5 flex items-center justify-center"><FileText className="w-8 h-8 text-navy/30" /></div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-navy truncate" data-testid="uploaded-document-name">{fileName}</p>
            <p className="text-xs text-emerald-600 mt-0.5">Uploaded{pageCount > 1 ? ` — ${pageCount} pages combined into one PDF` : ""}</p>
          </div>
          <button type="button" onClick={clear} aria-label="Remove document" data-testid="remove-document-btn"
            className="min-w-[44px] min-h-[44px] flex items-center justify-center text-navy/30 hover:text-red-500 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        {onOcrResult && (
          <button type="button" onClick={runOcr} disabled={scanning} className="w-full gm-btn-gold flex items-center justify-center gap-2 text-sm" data-testid="ocr-extract-trigger">
            {scanning ? <><Loader2 className="w-4 h-4 animate-spin" /> Extracting...</> : <><Sparkles className="w-4 h-4" /> Extract with AI</>}
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2.5">
        <OptionButton icon={Camera} primary title={`Capture & Crop ${noun}`} hint="Take photos, crop by hand, add more pages" testid="scan-document-btn"
          onClick={() => scanRef.current?.click()} disabled={disabled || uploading} />
        <OptionButton icon={Upload} title="Upload Photo" hint="Select an existing image" testid="upload-photo-btn"
          onClick={() => photoRef.current?.click()} disabled={disabled || uploading} />
        <OptionButton icon={FileUp} title="Upload PDF" hint="Select a PDF document" testid="upload-pdf-btn"
          onClick={() => pdfRef.current?.click()} disabled={disabled || uploading} />
        {uploading && (
          <div className="flex items-center justify-center gap-2 text-gold py-2"><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">Uploading...</span></div>
        )}
      </div>
      <input ref={scanRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="camera-capture-input" onChange={onCaptureFile} />
      <input ref={photoRef} type="file" accept="image/*" className="hidden" data-testid="invoice-scan-upload-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }} />
      <input ref={pdfRef} type="file" accept="application/pdf" className="hidden" data-testid="pdf-upload-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }} />
      <CaptureDialog open={!!captureSrc} initialSrc={captureSrc} title={`Capture & Crop ${noun}`} baseName={`${kind}-capture-${Date.now()}`}
        onClose={() => setCaptureSrc(null)} onConfirm={onCaptureConfirm} />
    </>
  );
}
